import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { buildNavigationCtx } from '@eu/app/router/appModule/navigation'
import { MSK_PRE_SIGNUP_STEPS } from '@shared/modules/assessment/router/selfTestStepSets.js'

// Onboarding progress is derived from route metadata (`meta.onboardingStep`).
// Steps are leaf-ranked: terminal screens share the highest rank and earlier
// routes are assigned from their longest path to a flow end. Forward movement
// therefore raises the bar, except for explicitly reviewed detours and lateral
// choices. Context-dependent steps (e.g. funnel-only screens and renewal reuse
// of Code Question) are functions of the navigation ctx.
//
// The step table is registered by routes.ts during module initialisation.
// This composable must NOT import routes.ts: guards and route declarations
// import it, and a routes → guards → progress → routes cycle leaves modules
// half-initialised in production builds (undefined guards, blank app).
let stepByRouteName = {}
let maxStep = 1

export const registerOnboardingSteps = (routes) => {
  stepByRouteName = Object.fromEntries(
    routes
      .filter(({ name, meta }) => name != null && meta?.onboardingStep != null)
      .map(({ name, meta }) => [name, meta.onboardingStep]),
  )
  const numericSteps = Object.values(stepByRouteName).filter(
    (step) => typeof step === 'number',
  )
  maxStep = Math.max(1, ...numericSteps)
}

const resolveStep = (step, ctx) =>
  typeof step === 'function' ? step(ctx) : step

// In the webOptimizedFunnel experiment the pre-signup assessment plus insurance
// selection, the covered screen, name input, and signup are prepended to the
// onboarding steps, shifting them all forward. The pre-signup routes declare
// negative steps (-3..0) so the unified formula
// (PRE_SIGNUP_STEPS + step) / (PRE_SIGNUP_STEPS + maxStep) works for every route.
const PRE_SIGNUP_STEPS = MSK_PRE_SIGNUP_STEPS.length + 3

export const getOnboardingProgress = (routeName, fallbackStep) => {
  const ctx = buildNavigationCtx()
  const step = resolveStep(stepByRouteName[routeName] ?? fallbackStep, ctx)

  if (step == null) return null

  if (ctx.isPreSubscriptionWebOptimizedFunnel) {
    return (PRE_SIGNUP_STEPS + step) / (PRE_SIGNUP_STEPS + maxStep)
  }

  return step / maxStep
}

export default function useOnboardingProgress() {
  const route = useRoute()
  // Keep this screen's progress stable when activation reloads the user.
  const progress = ref(
    getOnboardingProgress(route.name, route.meta?.onboardingStep),
  )
  return { progress }
}
